import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { url } from '../../config';
import styles from './ChatMessages.module.css';
import { Copy, Check } from 'lucide-react';

const ChatMessages = ({ messages, isLoading, selectedChat }) => {
  const messageStreamRef = useRef(null);
  const lastMsgRef = useRef(null);
  const [copiedMsgId, setCopiedMsgId] = useState(null);

  const handleCopy = (message, id) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = message;
    const textFormatted = (tempDiv.innerText || tempDiv.textContent).replace(
      /\\/g,
      ''
    );
    navigator.clipboard.writeText(textFormatted);

    setCopiedMsgId(id);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  const downloadFileFromS3 = async filepath => {
    axios
      .get(`${url}/api/chatbot/file-download/${filepath}/`, {
        responseType: 'blob',
      })
      .then(response => {
        const contentType = response.headers['content-type'];
        const blob = new Blob([response.data], { type: contentType });
        const downloadUrl = window.URL.createObjectURL(blob);
        if (contentType === 'application/pdf') {
          // If, pdf open file immediately then download to user device
          window.open(downloadUrl, '_blank');
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filepath;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filepath;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }
      })
      .catch(error => {
        alert('Download Failed: Unable to download file from server.');
      });
  };

  // Scroll to bottom for streaming chatbot response
  useEffect(() => {
    const msgStreamCtr = messageStreamRef.current;
    const lastMsg = lastMsgRef.current;
    if (msgStreamCtr && lastMsg) {
      const offsetTop = lastMsg.offsetTop;
      msgStreamCtr.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  }, [messages]);

  // Scroll to top whenever old chat is selected for viewing purposes
  useEffect(() => {
    const msgStreamCtr = messageStreamRef.current;
    if (msgStreamCtr && selectedChat) {
      msgStreamCtr.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [selectedChat]);

  useEffect(() => {
    const handleFileDownloadClick = e => {
      const target = e.target;
      if (target.matches('.download-link')) {
        e.preventDefault();
        const filepath = target.getAttribute('data-path');
        if (filepath) {
          downloadFileFromS3(filepath);
        }
      }
    };
    document.addEventListener('click', handleFileDownloadClick);
    return () => {
      document.removeEventListener('click', handleFileDownloadClick);
    };
  }, []);

  return (
    <>
      <div aria-live='assertive' className={styles.screenReaderOnly}>
        {isLoading
          ? 'System is typing'
          : messages?.[messages.length - 1]?.role === 'system'
          ? 'System response complete'
          : ''}
      </div>

      <div
        ref={messageStreamRef}
        className={styles.chatMessageStream}
        role='log'
        aria-live='off'>
        {selectedChat?.chatAsArr?.length > 0 ? (
          selectedChat?.chatAsArr?.map((msg, idx) => (
            <div
              key={idx}
              role='listitem'
              className={`${styles.chatBubble} ${
                msg.role == 'system' ? styles.system : styles.user
              }`}
              aria-label={`${
                msg.role === 'system' ? 'System message' : 'User message'
              }: ${msg.content}`}>
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                {typeof msg.content === 'string'
                  ? msg.content
                  : `Sorry, something went wrong`}
              </ReactMarkdown>
              <button
                className={styles.copyMsgToCBBtn}
                type='button'
                onClick={() => handleCopy(msg.content, idx)}>
                {copiedMsgId === idx ? <Check size={19} /> : <Copy size={19} />}
              </button>
            </div>
          ))
        ) : messages?.length > 0 ? (
          messages.map((msg, idx) => {
            const isLastMsg = idx === messages.length - 1;
            const showLoading = isLastMsg && msg.role === 'system' && isLoading;
            const isFinalSystemResponse =
              isLastMsg &&
              msg.role === 'system' &&
              !isLoading &&
              msg.content.length > 0;

            return (
              <div
                key={idx}
                role='listitem'
                ref={isLastMsg ? lastMsgRef : null}
                className={`${styles.chatBubble} ${
                  msg.role == 'system' ? styles.system : styles.user
                }`}
                aria-label={`${
                  msg.role === 'system' ? 'System message' : 'User message'
                }: ${msg.content}`}>
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {typeof msg.content === 'string'
                    ? msg.content
                    : `Sorry, something went wrong`}
                </ReactMarkdown>

                {showLoading && (
                  <div
                    className={styles.systemTypingIndicator}
                    aria-label='System is typing'>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}

                {isFinalSystemResponse && (
                  <div
                    aria-live='assertive'
                    className={styles.screenReaderOnly}>
                    System response complete: {msg.content}
                  </div>
                )}
                {!showLoading && (
                  <button
                    className={styles.copyMsgToCBBtn}
                    type='button'
                    onClick={() => handleCopy(msg.content, idx)}>
                    {copiedMsgId === idx ? (
                      <Check size={19} />
                    ) : (
                      <Copy size={19} />
                    )}
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <p className={styles.chatPlaceholderIntroTxt}>
            Hi! I'm WOMMI, your Council legislative assistant!
            <br />
            <br />
            <br />
            <span className={styles.disclaimer}>
              I'm here to make your legal research easier, so use me as a quick starting point. 
              That said, I'm not an attorney and my responses are sourced from Council legal memos. 
              If they have errors, I will relay them. Always double-check my statements and citations, 
              and treat my analysis as a helpful guide, not an authoritative source.
            </span>
          </p>
        )}
      </div>
    </>
  );
};

export default ChatMessages;
